import * as React from 'react';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MenuIcon from '@mui/icons-material/Menu';
import {
  AppBar,
  Box,
  Button,
  Drawer,
  GlobalStyles,
  IconButton,
  List,
  ListItem,
  ThemeProvider,
  Toolbar,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  Link,
  Route,
  Routes,
  matchPath,
  useLocation,
  useNavigate,
  useNavigationType,
} from 'react-router-dom';

import { usePreferences } from './accessibility/preferences';
import ReadingGuide from './accessibility/ReadingGuide';
import Footer from './components/Footer';
import ItemButton from './components/ItemButton';
import RouteMetadata from './components/RouteMetadata';
import routes from './data/Navigation';
import theme, { darkTheme, tokenStyles, tokens } from './theme';
import './App.css';

export default function Demo() {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const focusAfterClose = React.useRef<'main' | 'menu' | null>(null);
  const menuButtonRef = React.useRef<HTMLButtonElement>(null);
  const mainRef = React.useRef<HTMLElement>(null);
  const location = useLocation();
  const { contrast } = usePreferences();
  const previousLocation = React.useRef<typeof location | null>(null);
  const destinationRef = React.useRef<HTMLElement | null>(null);
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const isTabletOrDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const showFooter = routes.some(
    (route) =>
      route.footer && Boolean(matchPath({ path: route.path, end: true }, location.pathname))
  );

  React.useEffect(() => {
    if (isTabletOrDesktop && drawerOpen) {
      destinationRef.current = mainRef.current;
      focusAfterClose.current = 'main';
      setDrawerOpen(false);
    }
  }, [isTabletOrDesktop, drawerOpen]);

  React.useEffect(() => {
    const previous = previousLocation.current;
    if (
      previous?.key === location.key &&
      previous.pathname === location.pathname &&
      previous.hash === location.hash
    ) {
      return;
    }
    previousLocation.current = location;

    // Only published case-study articles are section destinations. Other fragments,
    // including the native skip link, keep their existing browser behavior.
    let section: HTMLElement | null = null;
    if (location.pathname.replace(/\/$/, '') === '/case-studies' && location.hash) {
      try {
        const candidate = document.getElementById(decodeURIComponent(location.hash.slice(1)));
        if (candidate?.matches('article[tabindex="-1"]') && mainRef.current?.contains(candidate)) {
          section = candidate;
        }
      } catch {
        // A malformed URL fragment should not interrupt navigation.
      }
    }

    const changed =
      previous && (previous.pathname !== location.pathname || previous.hash !== location.hash);
    if (!section && !changed) return;

    const destination = section ?? mainRef.current;
    destinationRef.current = destination;
    if (!previous || navigationType !== 'POP') {
      if (section) section.scrollIntoView({ block: 'start' });
      else window.scrollTo(0, 0);
    }

    if (drawerOpen || focusAfterClose.current) {
      focusAfterClose.current = 'main';
      if (drawerOpen) setDrawerOpen(false);
    } else {
      destination?.focus({ preventScroll: true });
    }
  }, [location, drawerOpen, navigationType]);

  const closeAndNavigate = (path: string) => {
    destinationRef.current = mainRef.current;
    focusAfterClose.current = 'main';
    setDrawerOpen(false);
    navigate(path);
  };

  const handleKeyPress = (path: string) => (event: React.KeyboardEvent) => {
    if (
      (event.key === 'Enter' || event.key === ' ') &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.shiftKey &&
      !event.altKey
    ) {
      event.preventDefault();
      closeAndNavigate(path);
    }
  };

  return (
    <ThemeProvider theme={contrast === 'dark' ? darkTheme : theme}>
      <GlobalStyles styles={tokenStyles} />
      <RouteMetadata />
      <a
        href='#main-content'
        className='skip-link'
      >
        Skip to main content
      </a>
      <AppBar
        position='sticky'
        elevation={0}
        sx={{
          // Stitch header: frosted surface, hairline shadow, 80px tall.
          backgroundColor: tokens.headerBg,
          backdropFilter: 'blur(24px)',
          color: tokens.onSurface,
          boxShadow: '0 1px 8px rgba(0, 0, 0, 0.04)',
          borderBottom: `1px solid ${tokens.headerBorder}`,
        }}
      >
        <Toolbar
          sx={{
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
            minHeight: { xs: 64, sm: 80 },
            boxSizing: 'border-box',
            width: '100%',
            // Same column as the page Container (MUI lg: 1200px wide including 24px gutters).
            maxWidth: 1200,
            mx: 'auto',
            px: { xs: 2, sm: 3 },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
            <IconButton
              ref={menuButtonRef}
              onClick={() => {
                focusAfterClose.current = null;
                setDrawerOpen(true);
              }}
              edge='start'
              color='inherit'
              aria-label='menu'
              aria-controls={drawerOpen && !isTabletOrDesktop ? 'mobile-navigation' : undefined}
              aria-expanded={drawerOpen && !isTabletOrDesktop}
              sx={{ mr: 1, display: { xs: 'inline-flex', sm: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            <Box
              component={Link}
              to='/'
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                minWidth: 0,
                textDecoration: 'none',
              }}
            >
              <Box
                component='img'
                src='/brand/mark.svg'
                alt=''
                aria-hidden='true'
                sx={{ height: 36, width: 36, flex: 'none' }}
              />
              <Box
                component='span'
                className='appBarTitle'
                sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1, minWidth: 0 }}
              >
                <Typography
                  component='span'
                  variant='h5'
                  sx={{
                    color: tokens.primary,
                    fontWeight: 700,
                    // Allowed to wrap on phones so enlarged text never overflows.
                    whiteSpace: { xs: 'normal', sm: 'nowrap' },
                  }}
                >
                  Scaled Tech
                </Typography>
                {/* The space keeps the link's accessible name "Scaled Tech Consulting". */}
                <Typography
                  component='span'
                  variant='labelCaps'
                  sx={{ color: tokens.accentText, whiteSpace: { xs: 'normal', sm: 'nowrap' } }}
                >
                  {' '}
                  Consulting
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box
            component='nav'
            aria-label='Primary navigation'
            sx={{
              display: { xs: 'none', sm: 'flex' },
              flexWrap: 'wrap',
              maxWidth: '100%',
              alignItems: 'center',
              columnGap: { sm: 2, md: 3 },
            }}
          >
            {routes
              .filter((route) => route.sidebar)
              .map((route) => {
                const selected = Boolean(
                  matchPath({ path: route.path, end: true }, location.pathname)
                );
                return (
                  <Typography
                    key={route.path}
                    component={Link}
                    to={route.path}
                    variant='labelMd'
                    aria-current={selected ? 'page' : undefined}
                    sx={{
                      color: selected ? tokens.accentText : tokens.onSurfaceVariant,
                      fontWeight: selected ? 600 : 500,
                      textDecoration: 'none',
                      whiteSpace: 'nowrap',
                      py: 0.5,
                      borderBottom: '2px solid',
                      borderColor: selected ? tokens.secondary : 'transparent',
                      '&:hover': { color: tokens.onSurface },
                    }}
                  >
                    {route.topLabel}
                  </Typography>
                );
              })}
          </Box>
          <Button
            component={Link}
            to='/contact-us#book'
            variant='contained'
            data-track='book_call'
            endIcon={<ArrowForwardIcon aria-hidden='true' />}
            sx={{ display: { xs: 'none', lg: 'inline-flex' }, whiteSpace: 'nowrap' }}
          >
            Book a call
          </Button>
        </Toolbar>
      </AppBar>
      <Drawer
        anchor='left'
        open={drawerOpen && !isTabletOrDesktop}
        onClose={() => {
          focusAfterClose.current = 'menu';
          setDrawerOpen(false);
        }}
        ModalProps={{ disableRestoreFocus: true }}
        PaperProps={{ sx: { scrollPaddingTop: '16px', scrollPaddingBottom: '16px' } }}
        SlideProps={{
          onExited: () => {
            if (focusAfterClose.current === 'main' || isTabletOrDesktop) {
              (destinationRef.current ?? mainRef.current)?.focus({ preventScroll: true });
            } else {
              menuButtonRef.current?.focus();
            }
            focusAfterClose.current = null;
          },
        }}
      >
        <Box
          component='nav'
          id='mobile-navigation'
          aria-label='Mobile navigation'
          sx={{ width: 280, pt: 2 }}
        >
          <List>
            {routes
              .filter((route) => route.sidebar)
              .map((route) => (
                <ListItem
                  key={route.path}
                  disablePadding
                >
                  <ItemButton
                    label1={route.label1}
                    label2={route.label2}
                    path={route.path}
                    icon={route.icon}
                    isSelected={Boolean(
                      matchPath({ path: route.path, end: true }, location.pathname)
                    )}
                    onClick={closeAndNavigate}
                    onPress={handleKeyPress}
                  />
                </ListItem>
              ))}
          </List>
        </Box>
      </Drawer>
      <Box
        component='main'
        id='main-content'
        ref={mainRef}
        tabIndex={-1}
        sx={{ minHeight: showFooter ? 'auto' : 'calc(100vh - 100px)' }}
      >
        <Routes>
          {routes.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={<route.component />}
            />
          ))}
        </Routes>
      </Box>
      {showFooter && <Footer />}
      <ReadingGuide />
    </ThemeProvider>
  );
}
